import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListChecks, RefreshCw, Loader2, Info, Send } from "lucide-react";
import { toast } from "sonner";

const KIND_LABELS = {
  test: "Prova", ticket: "Ticket", quote: "Preventivo", warranty: "Garanzia",
  welcome: "Benvenuto", accounting: "Contabilità", notification: "Notifica", generic: "—",
};
const DELIVERY = {
  sent: { label: "Inviata", cls: "bg-zinc-100 text-zinc-700" },
  delayed: { label: "In ritardo", cls: "bg-amber-100 text-amber-800" },
  delivered: { label: "Consegnata", cls: "bg-emerald-100 text-emerald-800" },
  opened: { label: "Aperta", cls: "bg-emerald-200 text-emerald-900" },
  clicked: { label: "Cliccata", cls: "bg-emerald-300 text-emerald-900" },
  bounced: { label: "Rimbalzata", cls: "bg-red-100 text-red-800" },
  complained: { label: "Spam", cls: "bg-red-200 text-red-900" },
  failed: { label: "Fallita", cls: "bg-red-100 text-red-800" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

function DeliveryBadge({ row }) {
  if (row.status === "failed") return <Badge className={`rounded-sm ${DELIVERY.failed.cls}`} data-testid="email-log-status">Fallita</Badge>;
  const d = DELIVERY[row.delivery_status] || DELIVERY.sent;
  return <Badge className={`rounded-sm ${d.cls}`} data-testid="email-log-status" title={row.delivery_reason || ""}>{d.label}</Badge>;
}

export function EmailLogCard() {
  const [data, setData] = useState({ items: [], stats: {} });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/settings/email-log", { params: { limit: 100, status: filter || undefined } });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const resend = async (row) => {
    setResending(row.id);
    try {
      await api.post(`/settings/email-log/${row.id}/resend`);
      toast.success(`Email reinviata a ${row.to}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); await load(); }
    finally { setResending(null); }
  };

  const { items, stats } = data;
  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="email-log-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center">
          <ListChecks className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Registro email inviate</h3>
          <p className="text-sm text-zinc-600">
            {stats.total ?? 0} totali · <span className={stats.failed ? "text-red-700 font-medium" : ""}>{stats.failed ?? 0} fallite</span> · <span className={stats.bounced ? "text-red-700 font-medium" : ""}>{stats.bounced ?? 0} rimbalzate</span>
          </p>
        </div>
        <div className="flex items-center gap-1">
          {[["", "Tutte"], ["sent", "Inviate"], ["failed", "Fallite"]].map(([v, l]) => (
            <Button key={v} data-testid={`email-log-filter-${v || "all"}`} size="sm" variant={filter === v ? "default" : "outline"}
              className={`rounded-sm ${filter === v ? "bg-brand hover:bg-brand-dark text-white" : ""}`} onClick={() => setFilter(v)}>{l}</Button>
          ))}
          <Button data-testid="email-log-refresh" variant="outline" size="sm" className="rounded-sm ml-1" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      {!stats.webhook_configured && (
        <div className="mb-4 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="email-log-webhook-hint">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="min-w-0 break-words">Tracking consegna/rimbalzi non attivo: configura su resend.com un webhook verso <code className="font-mono break-all">{`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/resend`}</code> (eventi email.*) e imposta <code className="font-mono">RESEND_WEBHOOK_SECRET</code> sul server.</span>
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="email-log-empty">Nessuna email registrata.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="email-log-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                <th className="px-3 py-2">Data</th><th className="px-3 py-2">Destinatario</th><th className="px-3 py-2">Oggetto</th>
                <th className="px-3 py-2">Tipo</th><th className="px-3 py-2">Canale</th><th className="px-3 py-2">Stato</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100 align-top" data-testid="email-log-row">
                  <td className="px-3 py-2 text-xs text-zinc-600 whitespace-nowrap">{fmtDate(r.sent_at)}</td>
                  <td className="px-3 py-2 text-zinc-800">{r.to}</td>
                  <td className="px-3 py-2 text-zinc-800 max-w-[320px]">
                    <div className="truncate" title={r.subject}>{r.subject}</div>
                    {r.error && <div className="text-[11px] text-red-700 mt-0.5 break-words" data-testid="email-log-error">{r.error}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-600">{KIND_LABELS[r.kind] || r.kind}</td>
                  <td className="px-3 py-2 text-xs text-zinc-600 whitespace-nowrap">{r.provider === "resend" ? "Resend" : "SMTP"}{r.fallback_from_resend ? " (fallback)" : ""}{r.attempts > 1 ? ` · ${r.attempts} tentativi` : ""}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col items-start gap-1">
                      <DeliveryBadge row={r} />
                      {r.status === "failed" && r.resendable && (
                        <Button data-testid="email-log-resend" size="sm" variant="outline" className="rounded-sm h-7 px-2 text-xs"
                          onClick={() => resend(r)} disabled={resending === r.id}>
                          {resending === r.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                          Reinvia
                        </Button>
                      )}
                      {r.resent_at && (
                        <span className={`text-[10px] ${r.resend_ok ? "text-emerald-700" : "text-red-700"}`} data-testid="email-log-resent-info">
                          {r.resend_ok ? "Reinviata" : "Reinvio fallito"} {fmtDate(r.resent_at)}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
